<template>
    <div>
        <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true" :height="150" :width="150"
            color="#FFFF00" background-color="#000000" :opacity="0.5" />

        <v-container>
            <v-form v-if="!isLoading">

                <div class="ack-main-card">

                <!-- PAGE TITLE -->
                <v-row class="mb-2">
                    <h2 class="pb-1">
                        {{ $t('acknowledgement.acknowledgemenfor') }}
                        <span class="page_title_value">
                            {{ acknowledgementName }}
                        </span>
                    </h2>
                </v-row>

                <!-- BACK BUTTON -->
                <v-row class="mb-4">
                    <v-btn @click="backButton" color="warning" class="white--text">
                        {{ $t('button.back') }}
                    </v-btn>
                </v-row>

                <!-- PIE / DONUT CHART -->
                <v-row>
                    <v-col cols="12" md="6" offset-md="3">
                        <div class="chart-wrapper">
                            <apexchart ref="pieChart" type="donut" height="380" :options="chartOptions"
                                :series="chartSeries" @dataPointSelection="onSliceClick" />
                            <!-- HAMBURGER MENU -->
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon class="chart-menu" v-on="on">
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item @click="downloadChart('svg')">
                                        <v-list-item-title>Download SVG</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="downloadChart('png')">
                                        <v-list-item-title>Download PNG</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="exportCSV">
                                        <v-list-item-title>Download CSV</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </v-col>
                </v-row>

                <!-- SUMMARY STAT CARDS — matching KPI Setting style -->
                <v-row class="mt-4 mb-4 px-2">
                    <v-col cols="12" class="pb-1">
                        <span class="review-stats-label">{{ $t('performancereport.totalstaff') }}</span>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex">
                        <div class="review-stat-card review-stat-initiated ack-stat-fill">
                            <div class="review-stat-label">{{ $t('performancereport.totalstaff') }}</div>
                            <div class="review-stat-value">{{ totalStaff }}</div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex">
                        <div class="review-stat-card review-stat-completed ack-stat-fill">
                            <div class="review-stat-label">{{ $t('acknowledgement.Accepted') }}</div>
                            <div class="review-stat-value">{{ totalAccepted }}</div>
                            <div class="review-stat-sub">{{ acceptedPercent }}%</div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex">
                        <div class="review-stat-card review-stat-ongoing ack-stat-fill">
                            <div class="review-stat-label">{{ $t('columns.pending') }}</div>
                            <div class="review-stat-value">{{ totalPending }}</div>
                            <div class="review-stat-sub">{{ pendingPercent }}%</div>
                        </div>
                    </v-col>
                </v-row>

                <v-row class="mt-n3 pb-4">
                    <v-col cols="12">
                        <v-data-table :headers="headers" :items="filteredItems" :search="search"
                            :items-per-page="itemsPerPage">
                            <template v-slot:top>
                                <!-- STICKY TABLE TITLE BAR -->
                                <div class="ack-table-header">
                                    <div class="ack-table-title">
                                        {{ $t('acknowledgement.acknowledgementacceptancesummary') }}
                                        <span v-if="filterStatus !== 'All'" class="ack-filter-badge"> – {{ filterStatus }}</span>
                                    </div>
                                    <div class="ack-table-actions">
                                        <button type="button" class="export-icon-btn export-icon-pdf" @click="printPDF" title="Export PDF">
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h2a2 2 0 0 1 0 4H9v-4z"/><path d="M15 13h1a2 2 0 0 1 0 4h-1"/><line x1="15" y1="17" x2="17" y2="17"/></svg>
                                          PDF
                                        </button>
                                        <button type="button" class="export-icon-btn export-icon-csv" @click="exportCSV" title="Export CSV">
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
                                          CSV
                                        </button>
                                        <v-text-field
                                            v-model="search"
                                            append-icon="search"
                                            label="Search"
                                            single-line
                                            hide-details
                                            outlined
                                            dense
                                            style="max-width:220px; min-width:180px;"
                                        ></v-text-field>
                                    </div>
                                </div>
                            </template>

                            <template v-slot:[`item.Status`]="{ item }">
                                <v-chip :color="item.Status === 'Accepted' ? 'green' : 'orange'" dark>
                                    {{ item.Status }}
                                </v-chip>
                            </template>

                            <template v-slot:[`item.acknowledgementDateTime`]="{ item }">
                                {{ formatDate(item.acknowledgementDateTime) }}
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
                </div>

            </v-form>

            <!-- ERROR DIALOG -->
            <div v-if="isErr">
                <app-error v-bind:parentError="{ data: this.errMsg, isErr: this.isErr }" v-on:closeError="isErr = $event" />
            </div>
        </v-container>
    </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import ErrorDialog from "../../../components/common/ErrorDialog";
import VueApexCharts from "vue-apexcharts";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

export default {
    components: {
        Loading,
        "app-error": ErrorDialog,
        apexchart: VueApexCharts,
    },

    data() {
        return {
            publicPath: process.env.BASE_URL,
            id: this.$route.params.id,
            activestaff: this.$route.params.activestaff,
            acknowledgementName: "",
            dataItems: [],
            search: "",
            filterStatus: "All",
            isLoading: false,
            isErr: false,
            errMsg: "",
            itemsPerPage: 10,

            headers: [
                { text: this.$t('columns.status'), value: "Status" },
                { text: this.$t('login.username'), value: "userid" },
                { text: this.$t('columns.staffcode'), value: "staffcode" },
                { text: this.$t('columns.staffname'), value: "psnname" },
                { text: this.$t('attendancereportbypeiordbycompanyreport.companyname'), value: "compnyname" },
                { text: this.$t('acknowledgement.acknowledgedon'), value: "acknowledgementDateTime" },
            ],
        };
    },

    computed: {
        filteredItems() {
            if (this.filterStatus === "All") return this.dataItems;
            return this.dataItems.filter((x) => x.Status === this.filterStatus);
        },
        totalStaff() {
            return this.dataItems.length;
        },
        totalAccepted() {
            return this.dataItems.filter((x) => x.Status === "Accepted").length;
        },
        totalPending() {
            return this.dataItems.filter((x) => x.Status === "Pending").length;
        },
        acceptedPercent() {
            return this.totalStaff === 0
                ? 0
                : ((this.totalAccepted / this.totalStaff) * 100).toFixed(1);
        },
        pendingPercent() {
            return this.totalStaff === 0
                ? 0
                : ((this.totalPending / this.totalStaff) * 100).toFixed(1);
        },
        chartSeries() {
            return [this.totalAccepted, this.totalPending];
        },
        chartOptions() {
            const total = this.totalStaff || 1;
            return {
                chart: {
                    type: "donut",
                    toolbar: { show: false },
                    events: {
                        dataPointMouseEnter: () => {
                            document.querySelector('.apexcharts-canvas').style.cursor = 'pointer';
                        },
                        dataPointMouseLeave: () => {
                            document.querySelector('.apexcharts-canvas').style.cursor = 'default';
                        }
                    }
                },
                title: {
                    text: this.$t('acknowledgement.acknowledgementacceptancesummary'),
                    align: 'center',
                    style: {
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }
                },
                labels: ["Accepted", "Pending"],
                colors: ["#4CAF50", "#FFC107"],
                tooltip: {
                    y: {
                        formatter: (val, opts) => {
                            const seriesIndex = opts?.seriesIndex ?? 0;
                            // get label safely
                            const label = opts?.w?.globals?.labels?.[seriesIndex] ?? (seriesIndex === 0 ? 'Accepted' : 'Pending');
                            const count = val;
                            const percentage = label === "Accepted" ? this.acceptedPercent : this.pendingPercent;
                            // return `${label}: ${count} (${percentage}%)`;
                            return `${count} (${percentage}%)`; // only one 'label:' now
                        }
                    }
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val, opts) => {
                        const count = opts.w.config.series[opts.seriesIndex];
                        const percentage = (count / total * 100).toFixed(2);
                        return `${count} / ${percentage}%`;
                    },
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '60%',
                            labels: {
                                show: true,
                                name: { show: true },
                                value: { show: true },
                                total: {
                                    show: true,
                                    label: this.$t('performancereport.totalstaff'),
                                    formatter: () => this.totalStaff
                                }
                            }
                        }
                    }
                },
                legend: { position: 'bottom' },
            };
        },
    },

    methods: {
        backButton() {
            try {
                this.$router.push({ name: "acknowledgementacceptancelisting" });
            } catch {
                this.$router.go(-1);
            }
        },

        formatDate(date) {
            if (!date) return "-";
            return moment(date).format("YYYY-MM-DD HH:mm");
        },

        async loadacknowledgementDetails() {
            try {
                this.isLoading = true;
                const res = await this.$store.dispatch("post", {
                    url: "/acknowledgementacceptanceSummary",
                    payload: { id: this.id, activestaff: this.activestaff },
                });
                this.acknowledgementName = res.data.acknowledgement.ackName || "";
                this.dataItems = res.data.details || [];
            } catch (err) {
                this.isErr = true;
                this.errMsg = err;
            } finally {
                this.isLoading = false;
            }
        },

        onSliceClick(event, chartContext, config) {
            const clickedLabel = this.chartOptions.labels[config.dataPointIndex];
            this.filterStatus = this.filterStatus === clickedLabel ? "All" : clickedLabel;
        },

        exportCSV() {
            const csvRows = [];
            const headers = this.headers.map((h) => h.text);
            csvRows.push(headers.join(","));

            for (const item of this.filteredItems) {
                const row = [
                    item.Status,
                    item.userid,
                    item.staffcode,
                    item.psnname,
                    item.compnyname,
                    this.formatDate(item.acknowledgementDateTime),
                ];
                csvRows.push(row.join(","));
            }

            const safeName = this.acknowledgementName
                .substring(0, 40)
                .replace(/[^\w\s]/g, "");
            const statusSuffix = this.filterStatus === "All" ? "" : ` - ${this.filterStatus}`;
            const fileName = `Acknowledgement Acceptance Summary - ${safeName}${statusSuffix}.csv`;

            const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute("download", fileName);
            link.click();
            URL.revokeObjectURL(url);
        },

        downloadChart(type) {
            const chart = this.$refs.pieChart.chart;
            chart.dataURI().then(({ imgURI, svg }) => {
                const data = type === "png" ? imgURI : svg;
                const link = document.createElement("a");
                link.href =
                    type === "png"
                        ? data
                        : "data:image/svg+xml;charset=utf-8," + encodeURIComponent(data);
                link.download = `acknowledgement_chart.${type}`;
                link.click();
            });
        },

        printPDF() {
            const doc = new jsPDF();
            const safeName = this.acknowledgementName
                .substring(0, 40)
                .replace(/[^\w\s]/g, "");
            const statusSuffix = this.filterStatus === "All" ? "" : ` - ${this.filterStatus}`;
            const fileName = `Acknowledgement Acceptance Summary - ${safeName}${statusSuffix}.pdf`;

            const headerTitle = `Acknowledgement Acceptance Summary – ${this.acknowledgementName}${this.filterStatus === "All" ? "" : ` (${this.filterStatus})`}`;
            doc.text(headerTitle, 14, 10);

            const tableData = this.filteredItems.map((item) => [
                item.Status,
                item.userid,
                item.staffcode,
                item.psnname,
                item.compnyname,
                this.formatDate(item.acknowledgementDateTime),
            ]);

            doc.autoTable({
                head: [this.headers.map((h) => h.text)],
                body: tableData,
                startY: 16,
            });

            doc.save(fileName);
        },
    },

    async mounted() {
        try {
            this.isLoading = true;
            await this.loadacknowledgementDetails();
        } catch (e) {
            this.isErr = true;
            this.errMsg = e.message || "Something went wrong during mounted";
        } finally {
            this.isLoading = false;
        }
    },
};
</script>

<style scoped>
.ack-main-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #E8EDF5;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    padding: 24px 28px 48px 28px;
    width: 100%;
}

.chart-wrapper {
    position: relative;
    cursor: pointer;
}
.chart-menu {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
}

/* Stat sub-label (percentage) */
.review-stat-sub {
    font-size: 0.85rem;
    font-weight: 600;
    opacity: 0.7;
    margin-top: 4px;
}

/* Equal height stat cards */
.ack-stat-fill {
    width: 100%;
    min-height: 110px;
}

/* Sticky table header bar */
.ack-table-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #E8EDF5;
    gap: 12px;
    flex-wrap: wrap;
}
.ack-table-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1E293B;
}
.ack-filter-badge {
    font-size: 0.85rem;
    font-weight: 500;
    color: #64748B;
}
.ack-table-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.export-icon-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 8px; border: none;
  font-size: 0.8rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: all 0.15s;
}
.export-icon-pdf { background: #FEF2F2; color: #DC2626; border: 1px solid #FECACA; }
.export-icon-pdf:hover { background: #DC2626; color: #fff; }
.export-icon-csv { background: #ECFDF5; color: #059669; border: 1px solid #A7F3D0; }
.export-icon-csv:hover { background: #059669; color: #fff; }

/* Allow sticky to work inside v-data-table */
.v-data-table >>> .v-data-table__wrapper {
    overflow: unset !important;
}
.v-data-table {
    overflow: visible !important;
}
</style>